import React, { useEffect, useId, useRef } from 'react';

import applyIcon from '../../../assets/applytoprofile-icon.png';
import editIcon from '../../../assets/editavatar-icon.png';
import renameIcon from '../../../assets/renameavatar-icon.png';
import deleteIcon from '../../../assets/deleteavatar-icon.png';

import '../styles/AvatarActionsDrawer.css';

export default function AvatarActionsDrawer({
  creation,
  busy = false,
  applying = false,
  canApply = true,
  error = '',
  onClose,
  onApply,
  onEdit,
  onRename,
  onDelete,
}) {
  const dialogRef = useRef(null);
  const titleId = useId();

  // Keep the latest callback without reopening the dialog.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!creation || !dialog) return;

    const previousFocus = document.activeElement;

    // Native modal provides focus trapping and blocks the page behind it.
    dialog.showModal();

    return () => {
      dialog.close();

      if (previousFocus?.isConnected) {
        previousFocus.focus({ preventScroll: true });
      }
    };
  }, [creation?._id]);

  function requestClose() {
    if (!busy) {
      closeRef.current?.();
    }
  }

  if (!creation) return null;

  const actions = [
    {
      id: 'apply',
      label: applying ? 'Applying…' : 'Apply to profile',
      icon: applyIcon,
      onClick: onApply,
      disabled: busy || !canApply,
    },
    {
      id: 'edit',
      label: 'Edit avatar',
      icon: editIcon,
      onClick: onEdit,
      disabled: busy,
    },
    {
      id: 'rename',
      label: 'Rename',
      icon: renameIcon,
      onClick: onRename,
      disabled: busy,
    },
    {
      id: 'delete',
      label: 'Delete avatar',
      icon: deleteIcon,
      onClick: onDelete,
      disabled: busy,
      destructive: true,
    },
  ];

  return (
    <dialog
      ref={dialogRef}
      className="avatar-actions-drawer"
      aria-labelledby={titleId}
      aria-busy={busy}
      onCancel={(event) => {
        // Handle Escape ourselves so an active request isn't dismissed.
        event.preventDefault();
        requestClose();
      }}
      onClick={(event) => {
        if (event.target !== event.currentTarget) return;

        const bounds = event.currentTarget.getBoundingClientRect();
        const outside =
          event.clientX < bounds.left ||
          event.clientX > bounds.right ||
          event.clientY < bounds.top ||
          event.clientY > bounds.bottom;

        if (outside) requestClose();
      }}
    >
      <div className="avatar-actions-drawer__handle" aria-hidden="true" />

      <header className="avatar-actions-drawer__header">
        <div className="avatar-actions-drawer__identity">
          <img
            className="avatar-actions-drawer__preview"
            src={creation.imageUrl}
            alt=""
            width="48"
            height="48"
          />

          <h2 id={titleId}>{creation.name}</h2>
        </div>

        <button
          type="button"
          className="avatar-actions-drawer__close"
          aria-label="Close avatar actions"
          disabled={busy}
          onClick={requestClose}
          autoFocus
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="m6 6 12 12M18 6 6 18" />
          </svg>
        </button>
      </header>

      <div className="avatar-actions-drawer__actions">
        {actions.map((action) => (
          <button
            key={action.id}
            type="button"
            className={`avatar-actions-drawer__action ${
              action.destructive ? 'is-destructive' : ''
            }`}
            disabled={
              action.disabled ||
              typeof action.onClick !== 'function'
            }
            onClick={() => action.onClick(creation)}
          >
            <img
              className="avatar-actions-drawer__icon"
              src={action.icon}
              alt=""
              width="24"
              height="24"
            />

            <span>{action.label}</span>
          </button>
        ))}
      </div>

      {applying && (
        <p className="avatar-actions-drawer__status" role="status">
          Updating your profile…
        </p>
      )}

      {error && (
        <p className="avatar-actions-drawer__error" role="alert">
          {error}
        </p>
      )}
    </dialog>
  );
}
